// ==== TYPES ====
import { IConfig } from "types/globalTypes";

type FeatureFlagPayload = IConfig["featureFlags"];

/**
 * Normalises a feature flag name so it can be matched irrespective of casing.
 *
 * Flag names are authored by hand in Azure App Configuration, while the names
 * the app asks for live in the `FEATURE_FLAGS` enum. Matching one against the
 * other with a raw object lookup is case-sensitive in JavaScript, so a casing
 * difference between the two yields `undefined` and reads as "off" — with no
 * error, no warning, and nothing to distinguish it from a flag that simply has
 * not been created yet. Every other layer of the stack resolves flag names
 * case-insensitively; normalising both sides makes the client behave the same
 * way.
 *
 * `toLowerCase` is deliberate. It is locale-invariant, whereas
 * `toLocaleLowerCase` folds "I" to a dotless variant under a Turkish locale and
 * would reintroduce the mismatch this guards against.
 *
 * All whitespace is removed, not just the leading and trailing kind. A flag name
 * never legitimately contains a space, so whitespace anywhere in one is an
 * authoring slip of exactly the same kind as a casing difference, and is treated
 * the same way. `\s` also covers the non-breaking space that copying a name out
 * of a web page tends to leave behind, which is invisible in the Azure portal.
 *
 * `findMalformedFeatureFlagNames` still reports the names this had to correct,
 * so the configuration can be tidied — but the feature resolves either way.
 */
export const normalizeFeatureFlagName = (name: string): string =>
  name?.replace(/\s/g, "").toLowerCase() ?? "";

const EMPTY_INDEX: ReadonlyMap<string, boolean> = new Map();

/**
 * Indexes already built, keyed by the payload they were built from.
 *
 * The index behind `AuthenticationStore.getFeatureFlag` is a MobX computed, so
 * it is only memoised while something observes it. Flags are also read outside
 * any reactive context — from plain helpers and from render callbacks — and
 * there the computed re-evaluates on every read. Without this cache each of
 * those reads would rebuild the whole index, turning a constant-time property
 * read into a full pass over the payload.
 *
 * A `WeakMap` keyed on the payload object is stale-proof for the same reason the
 * computed is: loading a new configuration produces a new object, which misses
 * the cache. It also holds no strong reference, so a replaced payload is
 * collectable. This assumes the payload is never mutated in place, which is true
 * — it is replaced wholesale when `/l/config` responds.
 */
const indexCache = new WeakMap<
  Record<string, unknown>,
  ReadonlyMap<string, boolean>
>();

/**
 * Builds a case-insensitive lookup over the flags returned by `/l/config`.
 *
 * Referentially transparent: the same payload always yields the same index, so
 * it is safe to call from the MobX computed that backs the lookup. Reporting on
 * the payload belongs in `findAmbiguousFeatureFlagGroups` and
 * `findMalformedFeatureFlagNames`.
 */
export const buildFeatureFlagIndex = (
  featureFlags: FeatureFlagPayload,
): ReadonlyMap<string, boolean> => {
  if (!featureFlags) {
    return EMPTY_INDEX;
  }

  const cached = indexCache.get(featureFlags);

  if (cached) {
    return cached;
  }

  const index = new Map<string, boolean>();

  Object.entries(featureFlags).forEach(([name, isEnabled]) => {
    const key = normalizeFeatureFlagName(name);

    // A name that normalises to nothing identifies no flag. Indexing it would
    // also answer lookups made with no flag name at all with whatever value it
    // happened to carry.
    if (!key) {
      return;
    }

    // When two names fold onto the same key, prefer the one already written in
    // canonical form over one that had to be folded. At most one name per key
    // can be canonical — two would be the same string, and an object cannot
    // carry the same key twice — so the winner never depends on the order the
    // payload happens to arrive in. Where no name is canonical the first wins,
    // which is equally stable.
    if (index.has(key) && name !== key) {
      return;
    }

    // Boolean() rather than a strict comparison, so any truthy shape the
    // payload might take keeps resolving exactly as it did before.
    index.set(key, Boolean(isEnabled));
  });

  indexCache.set(featureFlags, index);

  return index;
};

/**
 * Returns each group of published names that fold onto one flag name while
 * disagreeing on their value.
 *
 * Because names resolve case-insensitively and with whitespace removed, such a
 * group is one flag carrying more than one answer. `buildFeatureFlagIndex`
 * settles which name applies deterministically, but only one of them can, so the
 * group is still a defect at the source and callers should surface it.
 *
 * Grouped rather than reported name by name for two reasons. Three names folding
 * onto one flag are a single defect and read better as one message than as two.
 * And a running comparison against the last value seen would settle collisions
 * differently from `buildFeatureFlagIndex`, which prefers the canonically written
 * name; grouping avoids describing the conflict in terms of a resolution the
 * lookup does not actually apply.
 */
export const findAmbiguousFeatureFlagGroups = (
  featureFlags: FeatureFlagPayload,
): string[][] => {
  if (!featureFlags) {
    return [];
  }

  const grouped = new Map<string, { names: string[]; values: Set<boolean> }>();

  Object.entries(featureFlags).forEach(([name, isEnabled]) => {
    const key = normalizeFeatureFlagName(name);
    const group = grouped.get(key) ?? { names: [], values: new Set<boolean>() };

    group.names.push(name);
    group.values.add(Boolean(isEnabled));
    grouped.set(key, group);
  });

  return [...grouped.values()]
    .filter((group) => group.values.size > 1)
    .map((group) => group.names);
};

/**
 * Returns the published flag names that carry whitespace.
 *
 * These still resolve — `normalizeFeatureFlagName` strips the whitespace — but
 * the name is wrong at the source and should be corrected in Azure App
 * Configuration before it misleads someone reading the portal.
 */
export const findMalformedFeatureFlagNames = (
  featureFlags: FeatureFlagPayload,
): string[] => {
  if (!featureFlags) {
    return [];
  }

  return Object.keys(featureFlags).filter((name) => /\s/.test(name));
};
